import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faMedal,
  faCrown,
  faCalendarDays,
  faUser,
  faArrowLeft,
  faUserFriends,
  faEye,
  faChartBar,
  faListAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useUserById } from "../../hooks/user/useUserById";
import { useFollowStatus } from "../../hooks/user/useFollowStatus";
import { useFollowersList } from "../../hooks/user/useFollowersList";
import { useFollowingList } from "../../hooks/user/useFollowingList";
import PageHeader from "../../components/common/PageHeader";
import FollowButton from "../../components/user/FollowButton";
import VerificationBadge from "../../components/user/VerificationBadge";
import FollowersModal from "../../components/user/FollowersModal";
import SkeletonLoader, {
  ProfileStatsSkeleton,
} from "../../components/common/SkeletonLoader";

/**
 * UserProfile page component for viewing other users' profiles
 *
 * Features:
 * - Display user information, follower/following counts, and verification status
 * - Follow/unfollow button (hidden for own profile)
 * - User's league participation and performance statistics
 * - Handle user not found scenarios with appropriate error page
 * - URL parameter handling for user ID
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6
 */

const Stat = ({ icon, label, value, onClick, clickable = false }) => {
  let content;

  if (value == null) {
    content = "—";
  } else if (typeof value === "object" && !Array.isArray(value)) {
    // Special-case a rank-like object
    const { name, points, progress } = value;
    const formattedPoints =
      typeof points === "number" ? points.toFixed(2) : points;
    const header = [
      name,
      typeof points === "number" ? `${formattedPoints} pts` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    content = (
      <div className="w-full">
        <div className="text-dark-grey font-semibold">{header || "—"}</div>
        {typeof progress === "number" && (
          <div className="mt-1 h-2 bg-light-grey rounded">
            <div
              className="h-2 bg-deep-red rounded"
              style={{
                width: `${Math.max(0, Math.min(100, progress * 100))}%`,
              }}
            />
          </div>
        )}
      </div>
    );
  } else if (Array.isArray(value)) {
    content = value.join(", ");
  } else {
    content = String(value);
  }

  const StatContent = () => (
    <div className="flex items-center gap-3">
      <div className="shrink-0 rounded-full bg-light-grey p-2 shadow-card">
        <FontAwesomeIcon icon={icon} className="text-medium-grey" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-medium-grey">{label}</div>
        <div className="break-words">{content}</div>
      </div>
    </div>
  );

  if (clickable && onClick) {
    return (
      <button
        onClick={onClick}
        className="w-full text-left hover:bg-light-grey rounded-md p-2 -m-2 transition-colors"
      >
        <StatContent />
      </button>
    );
  }

  return <StatContent />;
};

const TABS = [
  { id: "overview", label: "Overview", icon: faUser },
  { id: "stats", label: "Stats", icon: faChartBar },
  { id: "leagues", label: "Leagues", icon: faListAlt },
];

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth0();
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [followersModalTab, setFollowersModalTab] = useState("followers");
  const [activeTab, setActiveTab] = useState("overview");

  // Validate userId format (MongoDB ObjectId is 24 hex characters)
  const isValidUserId = useMemo(() => {
    if (!userId) return false;
    // Check if it's a valid MongoDB ObjectId format (24 hex characters)
    return /^[0-9a-fA-F]{24}$/.test(userId);
  }, [userId]);

  // Fetch user data (only if userId is valid)
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useUserById(isValidUserId ? userId : null);

  // Get follow status for this user (only if userId is valid)
  const { data: followStatus } = useFollowStatus(isValidUserId ? userId : null);

  // Get follower and following counts (first page to get totals)
  const { data: followersData } = useFollowersList(
    isValidUserId ? userId : null,
    1,
    1,
  );
  const { data: followingData } = useFollowingList(
    isValidUserId ? userId : null,
    1,
    1,
  );

  // Determine if this is the current user's own profile
  const isOwnProfile = useMemo(() => {
    if (!currentUser || !user) return false;
    return currentUser.sub === user.auth0Id || currentUser.sub === user._id;
  }, [currentUser, user]);

  // Format join date
  const joinedDate = useMemo(() => {
    if (!user?.createdAt) return null;
    try {
      return new Date(user.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  }, [user?.createdAt]);

  // Handle navigation to followers/following lists
  const handleFollowersClick = () => {
    setFollowersModalTab("followers");
    setFollowersModalOpen(true);
  };

  const handleFollowingClick = () => {
    setFollowersModalTab("following");
    setFollowersModalOpen(true);
  };

  // Handle back navigation
  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  // Loading state with skeleton loader
  if (userLoading) {
    return (
      <div>
        <PageHeader
          title="Loading Profile..."
          subtitle="Loading user profile..."
          icon={faUser}
          backgroundIcon={faUser}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
          {/* Left column: Profile card skeleton */}
          <div className="lg:col-span-1 space-y-6 md:space-y-10">
            <div className="bg-white rounded-card shadow-card p-6">
              <div className="flex flex-col items-center text-center">
                {/* Avatar skeleton */}
                <SkeletonLoader variant="avatar" width="8rem" />

                {/* Identity skeleton */}
                <div className="mt-6 w-full">
                  <SkeletonLoader
                    variant="text"
                    width="60%"
                    height="1.25rem"
                    className="mx-auto mb-2"
                  />
                  <SkeletonLoader
                    variant="text"
                    width="40%"
                    height="1rem"
                    className="mx-auto"
                  />
                </div>

                {/* Follow button skeleton */}
                <div className="mt-4">
                  <SkeletonLoader
                    variant="button"
                    width="5rem"
                    height="2.25rem"
                  />
                </div>

                {/* Social stats skeleton */}
                <div className="w-full mt-6">
                  <ProfileStatsSkeleton />
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Performance skeleton */}
          <div className="lg:col-span-2 space-y-6 md:space-y-10">
            <div className="bg-white rounded-card shadow-card p-6">
              <SkeletonLoader
                variant="text"
                width="200px"
                height="1.125rem"
                className="mb-4"
              />
              <ProfileStatsSkeleton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid user ID format
  if (!isValidUserId) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-light-grey">
        <div className="p-6 max-w-md text-center bg-white rounded-card shadow-card">
          <div className="text-2xl text-deep-red mb-3">Invalid User ID</div>
          <p className="text-medium-grey mb-4">
            The user ID format is invalid. Please check the URL and try again.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleBackClick}
              className="px-4 py-2 bg-medium-grey hover:bg-dark-grey text-white rounded-md shadow transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate("/home")}
              className="px-4 py-2 bg-deep-red hover:bg-bright-red text-white rounded-md shadow transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User not found error
  if (userError || !user) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-light-grey">
        <div className="p-6 max-w-md text-center bg-white rounded-card shadow-card">
          <div className="text-2xl text-deep-red mb-3">User Not Found</div>
          <p className="text-medium-grey mb-4">
            {userError?.response?.status === 404
              ? "The user you're looking for doesn't exist or has been removed."
              : userError?.message || "Could not load user profile."}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleBackClick}
              className="px-4 py-2 bg-medium-grey hover:bg-dark-grey text-white rounded-md shadow transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate("/home")}
              className="px-4 py-2 bg-deep-red hover:bg-bright-red text-white rounded-md shadow transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get follower/following counts
  const followerCount =
    followersData?.pagination?.total ?? user.followerCount ?? 0;
  const followingCount =
    followingData?.pagination?.total ?? user.followingCount ?? 0;

  // League and performance data
  const leaguesCount =
    user?.activeLeagueCount ??
    user?.totalLeagues ??
    (Array.isArray(user?.leagues) ? user.leagues.length : undefined);
  const globalRank = user?.globalRank;

  return (
    <div>
      <PageHeader
        title={user.username || user.name || "User Profile"}
        subtitle={isOwnProfile ? "Your profile" : "User profile"}
        icon={faUser}
        backgroundIcon={faUser}
      />

      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={handleBackClick}
          className="inline-flex items-center gap-2 px-4 py-2 text-medium-grey hover:text-dark-grey transition-colors duration-200 bg-white rounded-md shadow-sm hover:shadow-md"
          aria-label="Go back"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back</span>
        </button>
      </div>

      {/* Profile Header Card */}
      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Avatar */}
          <div className="relative w-24 h-24 shrink-0">
            <img
              src={user?.picture || "/emblem-white.png"}
              alt={`${user?.username || user?.name || "User"}'s profile`}
              className="w-24 h-24 rounded-full object-cover ring-4 ring-light-grey"
              onError={(e) => {
                e.target.src = "/emblem-white.png";
              }}
            />
          </div>

          {/* Identity */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
              <div className="text-xl font-semibold text-dark-grey">
                {user?.username || user?.name || "User"}
              </div>
              {user?.verified && (
                <VerificationBadge
                  verified={user.verified}
                  size="large"
                  showTooltip={true}
                />
              )}
            </div>
            {user?.username &&
              user?.name &&
              user.username !== user.name &&
              user.name !== user.email && (
                <div className="text-medium-grey">{user.name}</div>
              )}
            {user?.role && (
              <span className="inline-flex mt-2 items-center px-3 py-1 text-xs font-medium rounded-full bg-light-gold text-gold shadow-card">
                <FontAwesomeIcon icon={faCrown} className="mr-2" />
                {user.role}
              </span>
            )}
            {/* Follow Button */}
            {!isOwnProfile && isValidUserId && (
              <div className="mt-3">
                <FollowButton
                  userId={userId}
                  initialFollowStatus={followStatus?.isFollowing || false}
                  size="md"
                  variant="primary"
                />
              </div>
            )}
          </div>

          {/* Social stats summary */}
          <div className="flex gap-6 shrink-0">
            <button
              className="text-center hover:bg-light-grey/50 rounded-lg px-3 py-2 transition-colors"
              onClick={handleFollowersClick}
            >
              <div className="text-xl font-bold text-deep-red">{followerCount}</div>
              <div className="text-xs text-medium-grey">Followers</div>
            </button>
            <button
              className="text-center hover:bg-light-grey/50 rounded-lg px-3 py-2 transition-colors"
              onClick={handleFollowingClick}
            >
              <div className="text-xl font-bold text-deep-red">{followingCount}</div>
              <div className="text-xs text-medium-grey">Following</div>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-dark-grey rounded-lg shadow-card mb-6 overflow-x-auto touch-pan-x">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 flex flex-col items-center transition-colors ${
              activeTab === tab.id
                ? "text-gold border-b-2 border-gold"
                : "text-white/80 hover:text-white"
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="mb-1" />
            <span className="text-sm font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rank & Info */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-semibold text-dark-grey mb-4">
              Rank &amp; Info
            </h3>
            <div className="space-y-4">
              <Stat
                icon={faMedal}
                label="Global Rank"
                value={globalRank ?? "—"}
              />
              <Stat icon={faCalendarDays} label="Joined" value={joinedDate} />
              <Stat
                icon={faUsers}
                label="Leagues"
                value={leaguesCount ?? "—"}
              />
            </div>
          </div>

          {/* Social */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-semibold text-dark-grey mb-4">
              Social
            </h3>
            <div className="space-y-4">
              <Stat
                icon={faUserFriends}
                label="Followers"
                value={followerCount}
                onClick={handleFollowersClick}
                clickable={true}
              />
              <Stat
                icon={faEye}
                label="Following"
                value={followingCount}
                onClick={handleFollowingClick}
                clickable={true}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "stats" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* League Performance */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h4 className="text-lg font-semibold text-dark-grey mb-4">
              League Performance
            </h4>
            <div className="space-y-4">
              <Stat
                icon={faMedal}
                label="Total Points"
                value={user?.totalPoints ?? "—"}
              />
              <Stat icon={faCrown} label="Wins" value={user?.wins ?? "—"} />
              <Stat
                icon={faUsers}
                label="Active Leagues"
                value={user?.activeLeagues ?? leaguesCount ?? "—"}
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h4 className="text-lg font-semibold text-dark-grey mb-4">
              Recent Activity
            </h4>
            <div className="space-y-4">
              <Stat
                icon={faCalendarDays}
                label="Last Active"
                value={
                  user?.lastActive
                    ? new Date(user.lastActive).toLocaleDateString()
                    : "—"
                }
              />
              <Stat
                icon={faEye}
                label="Profile Views"
                value={user?.profileViews ?? "—"}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "leagues" && (
        <div className="bg-white rounded-card shadow-card p-6">
          <h3 className="text-lg font-semibold text-dark-grey mb-4">
            League Participation
          </h3>

          {user?.leagues && user.leagues.length > 0 ? (
            <div className="space-y-3">
              {user.leagues.slice(0, 5).map((league, index) => (
                <div
                  key={league._id || index}
                  className="flex items-center justify-between p-3 bg-light-grey rounded-md"
                >
                  <div>
                    <div className="font-medium text-dark-grey">
                      {league.name || `League ${index + 1}`}
                    </div>
                    <div className="text-sm text-medium-grey">
                      {league.memberCount
                        ? `${league.memberCount} members`
                        : ""}
                    </div>
                  </div>
                  {league.rank && (
                    <div className="text-sm font-medium text-deep-red">
                      Rank #{league.rank}
                    </div>
                  )}
                </div>
              ))}
              {user.leagues.length > 5 && (
                <div className="text-center text-medium-grey text-sm">
                  And {user.leagues.length - 5} more leagues...
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <FontAwesomeIcon
                icon={faUsers}
                className="text-4xl text-medium-grey mb-4"
              />
              <p className="text-medium-grey">
                {isOwnProfile
                  ? "You haven't joined any leagues yet."
                  : "This user hasn't joined any leagues yet."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Followers/Following Modal */}
      <FollowersModal
        isOpen={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
        userId={userId}
        username={user?.username || user?.name}
        initialTab={followersModalTab}
        followerCount={followerCount}
        followingCount={followingCount}
      />
    </div>
  );
};

export default UserProfile;
